import { useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { useToast } from '../../hooks/useToast';
import {
    addTradingProvider,
    removeTradingProvider,
    selectSettings,
    selectTradingProviders
} from '../../store/settingsSlice';
import {
    getAvailableTradingProviders,
    TRADING_PROVIDERS
} from '../../services/providers';
import type { TradingProviderType } from '../../services/providers';

interface TradingProviderSectionProps {
    onSaveSettings: () => void;
    initialStateRef: React.MutableRefObject<{
        dataFeedProvider: string;
        tradingProvider: string;
        dataFeedConnected: boolean;
        connectedTradingProviders: Set<string>;
    }>;
}

const TradingProviderSection = ({ onSaveSettings }: TradingProviderSectionProps) => {
    const dispatch = useAppDispatch();
    const { user } = useAppSelector((state) => state.auth);
    const { showToast } = useToast();
    const settings = useAppSelector(selectSettings);
    const tradingProviders = useAppSelector(selectTradingProviders);

    console.log('TradingProviderSection render', { tradingProviders });

    const [isConnecting, setIsConnecting] = useState(false);
    const [editingProvider, setEditingProvider] = useState<string | null>(null);
    const [showDisconnectDialog, setShowDisconnectDialog] = useState<string | null>(null);
    const [showDeleteDialog, setShowDeleteDialog] = useState<string | null>(null);
    const [credentials, setCredentials] = useState<Record<string, string>>({});

    const [selectedProvider, setSelectedProvider] = useState<TradingProviderType>('' as TradingProviderType);

    const handleProviderChange = (providerType: TradingProviderType) => {
        setSelectedProvider(providerType);
        setCredentials({});
    };

    const handleCredentialChange = (key: string, value: string) => {
        setCredentials(prev => ({ ...prev, [key]: value }));
    };

    const handleConnect = async () => {

        setIsConnecting(true);
        try {
            const { ProviderFactory } = await import('../../services/providers');
            const provider = ProviderFactory.getTradingProvider(selectedProvider);
            if (!provider) {
                showToast('Selected provider is not available.', 'error');
                setIsConnecting(false);
                return;
            }

            // Connect to provider
            const trading = await provider.connect(credentials);

            if (!trading) {
                showToast('Failed to connect. Please check your credentials.', 'error');
                return;
            }

            dispatch(addTradingProvider(trading));

            showToast(`Connected to ${TRADING_PROVIDERS[selectedProvider]?.name}`, 'success');

            // Reset selection and credentials for next connection
            setSelectedProvider('' as TradingProviderType);
            setCredentials({});
            onSaveSettings();
        } catch (error) {
            console.error('[TradingProvider] Connection error:', error);
            showToast(`Failed to connect: ${error instanceof Error ? error.message : 'Unknown error'}`, 'error');
        } finally {
            setIsConnecting(false);
        }
    };

    const handleStartEdit = (connId: string) => {
        const provider = tradingProviders.find(p => p.id === connId);
        if (provider) {
            setEditingProvider(connId);
            setCredentials(provider.credentials);
        }
    };

    const handleSaveEdit = async () => {
        if (!editingProvider) return;

        const provider = tradingProviders.find(p => p.id === editingProvider);
        if (!provider) return;

        setIsConnecting(true);
        try {
            // Disconnect first if connected
            if (provider.status === 'connected') {
                const { ProviderFactory } = await import('../../services/providers');
                const providerInstance = ProviderFactory.getTradingProvider(provider.providerName as TradingProviderType);
                await providerInstance?.disconnect();
            }

            // Reconnect with new credentials
            const { ProviderFactory } = await import('../../services/providers');
            const providerInstance = ProviderFactory.getTradingProvider(provider.providerName as TradingProviderType);
            const updatedConnection = await providerInstance?.connect(credentials);

            if (!updatedConnection) {
                showToast('Failed to update credentials. Please check your credentials.', 'error');
                return;
            }

            showToast('Credentials updated successfully', 'success');
            setEditingProvider(null);
            setCredentials({});
            onSaveSettings();
        } catch (error) {
            console.error('[TradingProvider] Update error:', error);
            showToast(`Failed to update: ${error instanceof Error ? error.message : 'Unknown error'}`, 'error');
        } finally {
            setIsConnecting(false);
        }
    };

    const handleCancelEdit = () => {
        setEditingProvider(null);
        setCredentials({});
    };

    const handleDisconnect = async (connectionId: string) => {
        try {
            const provider = tradingProviders.find(p => p.id === connectionId);
            if (!provider) return;

            const { ProviderFactory } = await import('../../services/providers');
            const providerInstance = ProviderFactory.getTradingProvider(provider.providerName as TradingProviderType);
            await providerInstance?.disconnect();

            showToast('Disconnected from provider', 'info');
            setShowDisconnectDialog(null);
            onSaveSettings();
        } catch (error) {
            console.error('[TradingProvider] Disconnect error:', error);
            showToast(`Failed to disconnect: ${error instanceof Error ? error.message : 'Unknown error'}`, 'error');
            setShowDisconnectDialog(null);
        }
    };

    const handleDelete = async (connectionId: string) => {
        try {
            dispatch(removeTradingProvider(connectionId));
            showToast('Provider connection deleted', 'info');
            setShowDeleteDialog(null);
            setEditingProvider(null);
            setCredentials({});
            onSaveSettings();
        } catch (error) {
            console.error('[TradingProvider] Delete error:', error);
            showToast(`Failed to delete: ${error instanceof Error ? error.message : 'Unknown error'}`, 'error');
            setShowDeleteDialog(null);
        }
    };

    const handleReconnect = async (connectionId: string) => {
        if (connectionId) {
            const provider = tradingProviders.find(p => p.id === connectionId);
            if (!provider) return;

            try {
                const { ProviderFactory } = await import('../../services/providers');
                const providerInstance = ProviderFactory.getTradingProvider(provider.providerName as TradingProviderType);
                if (!providerInstance) throw new Error('Unsupported provider for reconnection');
                providerInstance.updateCredentials(provider.credentials)
                await providerInstance.connect(provider.credentials);
                showToast(`Reconnected to ${TRADING_PROVIDERS[provider.providerName as TradingProviderType]?.name}`, 'success');
                onSaveSettings();
            } catch (err) {
                console.error('[TradingProvider] Reconnection error:', err);
                showToast(`Failed to reconnect: ${err instanceof Error ? err.message : 'Unknown error'}`, 'error');
            }
        }
    };

    const providerConfig = selectedProvider ? TRADING_PROVIDERS[selectedProvider] : null;
    const requiredFields = providerConfig?.credentialFields?.filter(f => f.required) || [];
    const missingRequired = requiredFields.some(field =>
        !credentials[field.key] || credentials[field.key] === ''
    );

    return (
        <div style={{
            backgroundColor: 'var(--color-surface)',
            border: '1px solid var(--color-border-light)',
            borderRadius: 'var(--radius-lg)',
            padding: 'var(--spacing-lg)',
            boxShadow: 'var(--shadow-sm)'
        }}>
            {/* Header */}
            <div style={{
                marginBottom: 'var(--spacing-lg)',
                paddingBottom: 'var(--spacing-lg)',
                borderBottom: '1px solid var(--color-border-light)'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-md)', marginBottom: tradingProviders.length > 0 ? 'var(--spacing-md)' : 0 }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary-600)">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                    </svg>
                    <h2 style={{
                        fontSize: '1.25rem',
                        fontWeight: '600',
                        color: 'var(--color-text-primary)',
                        margin: 0
                    }}>
                        Trading Provider
                    </h2>
                </div>

                {/* Connected Providers Chips */}
                {tradingProviders.length > 0 && (
                    <div style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 'var(--spacing-sm)'
                    }}>
                        {tradingProviders.map(provider => (
                            <div
                                key={provider.id}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 'var(--spacing-sm)',
                                    padding: 'var(--spacing-sm) var(--spacing-md)',
                                    backgroundColor: provider.status === 'connected' ? 'var(--color-success-50)' : 'var(--color-danger-50)',
                                    border: provider.status === 'connected' ? '1px solid var(--color-success-200)' : '1px solid var(--color-danger-200)',
                                    borderRadius: 'var(--radius-lg)',
                                    fontSize: '0.875rem',
                                    fontWeight: '500',
                                    color: provider.status === 'connected' ? 'var(--color-success-700)' : 'var(--color-danger-700)'
                                }}
                            >
                                <div style={{
                                    width: '8px',
                                    height: '8px',
                                    borderRadius: '50%',
                                    backgroundColor: provider.status === 'connected' ? 'var(--color-success-600)' : 'var(--color-danger-600)'
                                }} />
                                <span>{TRADING_PROVIDERS[provider.providerName as TradingProviderType]?.name}</span>

                                {/* Show Connect button only for disconnected providers */}
                                {provider.status !== 'connected' && (
                                    <button
                                        onClick={() => handleReconnect(provider.id)}
                                        disabled={isConnecting}
                                        style={{
                                            padding: 'var(--spacing-xs)',
                                            backgroundColor: 'transparent',
                                            border: 'none',
                                            cursor: isConnecting ? 'not-allowed' : 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            color: 'var(--color-danger-700)',
                                            transition: 'all 200ms',
                                            borderRadius: 'var(--radius-md)',
                                            opacity: isConnecting ? 0.5 : 1
                                        }}
                                        onMouseEnter={(e) => {
                                            if (!isConnecting) {
                                                e.currentTarget.style.backgroundColor = 'var(--color-danger-100)';
                                                e.currentTarget.style.color = 'var(--color-success-600)';
                                            }
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.backgroundColor = 'transparent';
                                            e.currentTarget.style.color = 'var(--color-danger-700)';
                                        }}
                                        title="Reconnect"
                                    >
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                        </svg>
                                    </button>
                                )}

                                {/* Edit button */}
                                <button
                                    onClick={() => handleStartEdit(provider.id)}
                                    style={{
                                        padding: '2px',
                                        backgroundColor: 'transparent',
                                        border: 'none',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        color: provider.status === 'connected' ? 'var(--color-success-700)' : 'var(--color-danger-700)',
                                        transition: 'color 200ms'
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.color = provider.status === 'connected' ? 'var(--color-success-800)' : 'var(--color-danger-800)'}
                                    onMouseLeave={(e) => e.currentTarget.style.color = provider.status === 'connected' ? 'var(--color-success-700)' : 'var(--color-danger-700)'}
                                    title="Edit credentials"
                                >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                    </svg>
                                </button>

                                {/* Disconnect button - only show when connected */}
                                {provider.status === 'connected' && (
                                    <button
                                        onClick={() => setShowDisconnectDialog(provider.id)}
                                        style={{
                                            padding: '2px',
                                            backgroundColor: 'transparent',
                                            border: 'none',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            color: 'var(--color-success-700)',
                                            transition: 'color 200ms'
                                        }}
                                        onMouseEnter={(e) => e.currentTarget.style.color = 'var(--color-warning-600)'}
                                        onMouseLeave={(e) => e.currentTarget.style.color = 'var(--color-success-700)'}
                                        title="Disconnect"
                                    >
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                                        </svg>
                                    </button>
                                )}

                                {/* Delete button */}
                                <button
                                    onClick={() => setShowDeleteDialog(provider.id)}
                                    style={{
                                        padding: '2px',
                                        backgroundColor: 'transparent',
                                        border: 'none',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        color: provider.status === 'connected' ? 'var(--color-success-700)' : 'var(--color-danger-700)',
                                        transition: 'color 200ms'
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.color = 'var(--color-danger-600)'}
                                    onMouseLeave={(e) => e.currentTarget.style.color = provider.status === 'connected' ? 'var(--color-success-700)' : 'var(--color-danger-700)'}
                                    title="Delete connection"
                                >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-md)' }}>
                {/* Provider Selection */}
                {!editingProvider && (
                    <div>
                        <label style={{
                            display: 'block',
                            marginBottom: 'var(--spacing-xs)',
                            fontSize: '0.875rem',
                            fontWeight: '500',
                            color: 'var(--color-text-secondary)'
                        }}>
                            Provider
                        </label>
                        <select
                            value={selectedProvider}
                            onChange={(e) => handleProviderChange(e.target.value as TradingProviderType)}
                            style={{
                                width: '100%',
                                padding: 'var(--spacing-sm) var(--spacing-md)',
                                borderRadius: 'var(--radius-md)',
                                border: '1px solid var(--color-border-light)',
                                backgroundColor: 'var(--color-surface)',
                                fontSize: '0.875rem',
                                color: 'var(--color-text-primary)',
                                cursor: 'pointer',
                                transition: 'border-color 200ms ease-in-out'
                            }}
                            onFocus={(e) => e.currentTarget.style.borderColor = 'var(--color-primary-600)'}
                            onBlur={(e) => e.currentTarget.style.borderColor = 'var(--color-border-light)'}
                        >
                            <option value="">Select a provider...</option>
                            {getAvailableTradingProviders(settings.country).map(provider => (
                                <option key={provider.id} value={provider.id}>
                                    {provider.name}
                                </option>
                            ))}
                        </select>
                        {selectedProvider && providerConfig && (
                            <p style={{
                                fontSize: '0.75rem',
                                color: 'var(--color-text-secondary)',
                                margin: 'var(--spacing-xs) 0 0 0'
                            }}>
                                {providerConfig.description}
                            </p>
                        )}
                    </div>
                )}

                {/* Editing existing provider */}
                {editingProvider && (
                    <div style={{
                        padding: 'var(--spacing-sm)',
                        backgroundColor: 'var(--color-primary-50)',
                        border: '1px solid var(--color-primary-200)',
                        borderRadius: 'var(--radius-md)',
                        marginBottom: 'var(--spacing-sm)'
                    }}>
                        <p style={{
                            fontSize: '0.875rem',
                            color: 'var(--color-primary-700)',
                            margin: 0,
                            fontWeight: '500'
                        }}>
                            Editing: {TRADING_PROVIDERS[tradingProviders.find(p => p.id === editingProvider)?.providerName as TradingProviderType]?.name}
                        </p>
                    </div>
                )}

                {/* Credential Fields */}
                {(selectedProvider || editingProvider) && (() => {
                    // Get the provider name - either from selectedProvider or from the editing provider connection
                    const providerName = editingProvider
                        ? tradingProviders.find(p => p.id === editingProvider)?.providerName as TradingProviderType
                        : selectedProvider;

                    return TRADING_PROVIDERS[providerName]?.credentialFields?.map((field) => {
                        const fieldValue = field.valueGenerator
                            ? field.valueGenerator(user?.userId)
                            : (field.value !== undefined ? field.value : credentials[field.key] || '');

                        return (
                            <div key={field.key}>
                                <label style={{
                                    display: 'block',
                                    marginBottom: 'var(--spacing-xs)',
                                    fontSize: '0.8125rem',
                                    fontWeight: '500',
                                    color: 'var(--color-text-secondary)'
                                }}>
                                    {field.label} {field.required && <span style={{ color: 'var(--color-danger-600)' }}>*</span>}
                                </label>
                                <input
                                    type={field.type}
                                    value={fieldValue}
                                    onChange={(e) => !field.readonly && handleCredentialChange(field.key, e.target.value)}
                                    disabled={field.readonly}
                                    readOnly={field.readonly}
                                    placeholder={field.placeholder}
                                    style={{
                                        width: '100%',
                                        padding: 'var(--spacing-sm) var(--spacing-md)',
                                        borderRadius: 'var(--radius-md)',
                                        border: '1px solid var(--color-border-light)',
                                        backgroundColor: field.readonly ? 'var(--color-gray-50)' : 'var(--color-surface)',
                                        fontSize: '0.875rem',
                                        color: 'var(--color-text-primary)',
                                        cursor: field.readonly ? 'default' : 'text',
                                        transition: 'border-color 200ms ease-in-out'
                                    }}
                                    onFocus={(e) => !field.readonly && (e.currentTarget.style.borderColor = 'var(--color-primary-600)')}
                                    onBlur={(e) => e.currentTarget.style.borderColor = 'var(--color-border-light)'}
                                />
                            </div>
                        );
                    });
                })()}

                {/* API credentials link */}
                {(() => {
                    const providerName = editingProvider
                        ? tradingProviders.find(p => p.id === editingProvider)?.providerName as TradingProviderType
                        : selectedProvider;
                    const config = providerName ? TRADING_PROVIDERS[providerName] : null;

                    return (selectedProvider || editingProvider) && config?.website && config?.credentialFields?.length > 0 && (
                        <a
                            href={config.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 'var(--spacing-xs)',
                                fontSize: '0.75rem',
                                color: 'var(--color-primary-600)',
                                textDecoration: 'none'
                            }}
                        >
                            Get API credentials from {config.name}
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                            </svg>
                        </a>
                    );
                })()}

                {/* Action Buttons */}
                {editingProvider ? (
                    <div style={{ display: 'flex', gap: 'var(--spacing-sm)' }}>
                        <button
                            onClick={handleSaveEdit}
                            style={{
                                flex: 1,
                                padding: 'var(--spacing-sm) var(--spacing-md)',
                                backgroundColor: 'var(--color-primary-600)',
                                border: 'none',
                                borderRadius: 'var(--radius-md)',
                                cursor: 'pointer',
                                fontSize: '0.875rem',
                                fontWeight: '600',
                                color: 'white',
                                transition: 'all 200ms ease-in-out'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = 'var(--color-primary-700)';
                                e.currentTarget.style.transform = 'translateY(-1px)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'var(--color-primary-600)';
                                e.currentTarget.style.transform = 'translateY(0)';
                            }}
                        >
                            Save Changes
                        </button>
                        <button
                            onClick={handleCancelEdit}
                            style={{
                                padding: 'var(--spacing-sm) var(--spacing-md)',
                                backgroundColor: 'var(--color-surface)',
                                border: '1px solid var(--color-border-light)',
                                borderRadius: 'var(--radius-md)',
                                cursor: 'pointer',
                                fontSize: '0.875rem',
                                fontWeight: '500',
                                color: 'var(--color-text-primary)',
                                transition: 'all 200ms ease-in-out'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--color-gray-50)'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--color-surface)'}
                        >
                            Cancel
                        </button>
                    </div>
                ) : selectedProvider && (
                    <button
                        onClick={() => handleConnect()}
                        disabled={isConnecting || missingRequired}
                        style={{
                            width: '100%',
                            padding: 'var(--spacing-sm) var(--spacing-md)',
                            backgroundColor: 'var(--color-primary-600)',
                            border: 'none',
                            borderRadius: 'var(--radius-md)',
                            cursor: (isConnecting || missingRequired) ? 'not-allowed' : 'pointer',
                            fontSize: '0.875rem',
                            fontWeight: '600',
                            color: 'white',
                            transition: 'all 200ms ease-in-out',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 'var(--spacing-xs)',
                            opacity: (isConnecting || missingRequired) ? 0.5 : 1
                        }}
                        onMouseEnter={(e) => {
                            if (!isConnecting && !missingRequired) {
                                e.currentTarget.style.backgroundColor = 'var(--color-primary-700)';
                                e.currentTarget.style.transform = 'translateY(-1px)';
                            }
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'var(--color-primary-600)';
                            e.currentTarget.style.transform = 'translateY(0)';
                        }}
                    >
                        {isConnecting ? (
                            <>
                                <div style={{
                                    width: '16px',
                                    height: '16px',
                                    border: '2px solid white',
                                    borderTopColor: 'transparent',
                                    borderRadius: '50%',
                                    animation: 'spin 0.6s linear infinite'
                                }} />
                                Connecting...
                            </>
                        ) : (
                            <>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                </svg>
                                Connect
                            </>
                        )}
                    </button>
                )}
            </div>

            {/* Disconnect Confirmation Dialog */}
            {showDisconnectDialog && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000
                }}>
                    <div style={{
                        backgroundColor: 'var(--color-surface)',
                        borderRadius: 'var(--radius-lg)',
                        padding: 'var(--spacing-lg)',
                        maxWidth: '400px',
                        width: '90%',
                        boxShadow: 'var(--shadow-lg)'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)', marginBottom: 'var(--spacing-md)' }}>
                            <div style={{
                                width: '40px',
                                height: '40px',
                                borderRadius: '50%',
                                backgroundColor: 'var(--color-warning-100)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--color-warning-600)">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                            </div>
                            <h3 style={{ margin: 0, fontSize: '1.125rem', fontWeight: '600' }}>
                                Disconnect Provider
                            </h3>
                        </div>
                        <p style={{ margin: '0 0 var(--spacing-lg) 0', color: 'var(--color-text-secondary)', fontSize: '0.875rem', lineHeight: '1.5' }}>
                            Disconnect from <strong>{TRADING_PROVIDERS[tradingProviders.find(p => p.id === showDisconnectDialog)?.providerName as TradingProviderType]?.name}</strong>?
                            The connection will be preserved and you can reconnect anytime.
                        </p>
                        <div style={{ display: 'flex', gap: 'var(--spacing-sm)', justifyContent: 'flex-end' }}>
                            <button
                                onClick={() => setShowDisconnectDialog(null)}
                                style={{
                                    padding: 'var(--spacing-sm) var(--spacing-md)',
                                    backgroundColor: 'var(--color-surface)',
                                    border: '1px solid var(--color-border-light)',
                                    borderRadius: 'var(--radius-md)',
                                    cursor: 'pointer',
                                    fontSize: '0.875rem',
                                    fontWeight: '500',
                                    color: 'var(--color-text-primary)'
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => handleDisconnect(showDisconnectDialog)}
                                style={{
                                    padding: 'var(--spacing-sm) var(--spacing-md)',
                                    backgroundColor: 'var(--color-warning-600)',
                                    border: 'none',
                                    borderRadius: 'var(--radius-md)',
                                    cursor: 'pointer',
                                    fontSize: '0.875rem',
                                    fontWeight: '600',
                                    color: 'white'
                                }}
                            >
                                Disconnect
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Dialog */}
            {showDeleteDialog && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000
                }}>
                    <div style={{
                        backgroundColor: 'var(--color-surface)',
                        borderRadius: 'var(--radius-lg)',
                        padding: 'var(--spacing-lg)',
                        maxWidth: '400px',
                        width: '90%',
                        boxShadow: 'var(--shadow-lg)'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)', marginBottom: 'var(--spacing-md)' }}>
                            <div style={{
                                width: '40px',
                                height: '40px',
                                borderRadius: '50%',
                                backgroundColor: 'var(--color-danger-100)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--color-danger-600)">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                            </div>
                            <h3 style={{ margin: 0, fontSize: '1.125rem', fontWeight: '600' }}>
                                Delete Connection
                            </h3>
                        </div>
                        <p style={{ margin: '0 0 var(--spacing-lg) 0', color: 'var(--color-text-secondary)', fontSize: '0.875rem', lineHeight: '1.5' }}>
                            Permanently delete the connection to <strong>{TRADING_PROVIDERS[tradingProviders.find(p => p.id === showDeleteDialog)?.providerName as TradingProviderType]?.name}</strong>?
                            You'll need to re-enter your credentials to connect again.
                        </p>
                        <div style={{ display: 'flex', gap: 'var(--spacing-sm)', justifyContent: 'flex-end' }}>
                            <button
                                onClick={() => setShowDeleteDialog(null)}
                                style={{
                                    padding: 'var(--spacing-sm) var(--spacing-md)',
                                    backgroundColor: 'var(--color-surface)',
                                    border: '1px solid var(--color-border-light)',
                                    borderRadius: 'var(--radius-md)',
                                    cursor: 'pointer',
                                    fontSize: '0.875rem',
                                    fontWeight: '500',
                                    color: 'var(--color-text-primary)'
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => handleDelete(showDeleteDialog)}
                                style={{
                                    padding: 'var(--spacing-sm) var(--spacing-md)',
                                    backgroundColor: 'var(--color-danger-600)',
                                    border: 'none',
                                    borderRadius: 'var(--radius-md)',
                                    cursor: 'pointer',
                                    fontSize: '0.875rem',
                                    fontWeight: '600',
                                    color: 'white'
                                }}
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TradingProviderSection;
